import menuItems from "@/assets/menu.json";

export const APP_TITLE = "Portal do TCE";

export type PageTitleRoute = {
  path?: string;
  name?: string | null;
  meta?: Record<string, unknown>;
};

type MenuItem = {
  href?: string;
  title?: string;
  external?: boolean;
  child?: MenuItem[];
};

const menuTitleByPath = buildMenuTitleIndex(menuItems as MenuItem[]);

const routeTitleByName: Record<string, string> = {
  "tocar-senha": "Trocar Senha",
  criarProtocolo: "Criar Protocolo",
  "detalhe-protocolo-processo": "Detalhes do Protocolo/Processo",
  "detalhes-comunicacao-eletronica": "Detalhes da Comunicação Eletrônica",
  ResponderComunicacaoEletronica: "Responder à Comunicação Eletrônica",
};

/**
 * Resolve o nome da tela sem exigir que cada view repita o seu título.
 * A ordem de prioridade é: meta da rota, item do menu e nome da rota.
 */
export function resolvePageTitle(route?: PageTitleRoute): string {
  const routeTitle = route?.meta?.title;
  if (typeof routeTitle === "string" && routeTitle.trim()) {
    return routeTitle.trim();
  }

  const menuTitle = route?.path ? findMenuTitle(route.path) : "";
  if (menuTitle) return menuTitle;

  return route?.name ? findRouteTitle(route.name) : "";
}

/** Atualiza o título da aba no formato "Nome - Portal do TCE". */
export function setPageDocumentTitle(route?: PageTitleRoute): string {
  const pageTitle = resolvePageTitle(route);

  if (typeof document !== "undefined") {
    document.title = pageTitle ? pageTitle + " - " + APP_TITLE : APP_TITLE;
  }

  return pageTitle;
}

function buildMenuTitleIndex(items: MenuItem[]) {
  const index = new Map<string, string>();

  const visit = (item: MenuItem) => {
    const title = item.title === "__USER__" ? "Perfil" : item.title;

    if (item.href && title && !item.external && item.href.startsWith("/")) {
      index.set(normalizePath(item.href), title);
    }

    item.child?.forEach(visit);
  };

  items.forEach(visit);
  return index;
}

function findMenuTitle(path: string): string {
  const normalizedPath = normalizePath(path);
  const matchingPath = [...menuTitleByPath.keys()]
    .filter(
      (menuPath) =>
        normalizedPath === menuPath ||
        normalizedPath.startsWith(menuPath + "/")
    )
    .sort((a, b) => b.length - a.length)[0];

  return matchingPath ? menuTitleByPath.get(matchingPath) ?? "" : "";
}

function findRouteTitle(name: string): string {
  const normalizedName = name.trim();
  if (!normalizedName) return "";

  return (
    routeTitleByName[normalizedName] ??
    normalizedName
      .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
      .replace(/[-_]+/g, " ")
      .split(/\s+/)
      .filter(Boolean)
      .map(
        (word) =>
          word.charAt(0).toLocaleUpperCase("pt-BR") + word.slice(1)
      )
      .join(" ")
  );
}

function normalizePath(path: string): string {
  return ("/" + path).replace(/\/+/g, "/").replace(/\/$/, "") || "/";
}
